import { translate } from "./i18n";
import { getShotDisplayNumber } from "./shotNumber";
import { getSceneNumberLabel } from "./parseScenes";
import {
  effectiveFrameFormatValue,
  effectiveColorFormatValue,
  getShotFrameFormat,
  getShotColorFormat,
} from "./frameFormats";

export function dateStamp() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return { y, m, d };
}

export function safeInt(n, fallback = 0) {
  const x = Number(n);
  return Number.isFinite(x) ? x : fallback;
}

export function inferImageInfo(shot) {
  const img = shot?.image;
  const dataUrl = typeof img === "string" ? img : img?.dataUrl || "";
  const type =
    typeof img === "object" && img?.type
      ? img.type
      : dataUrl.startsWith("data:")
      ? dataUrl.slice(5, dataUrl.indexOf(";"))
      : "";
  return { dataUrl, type };
}

export function extFromMime(mime) {
  const m = (mime || "").toLowerCase();
  if (m.includes("png")) return "png";
  if (m.includes("jpeg") || m.includes("jpg")) return "jpg";
  if (m.includes("webp")) return "webp";
  return "png";
}

export function excelExtFromMime(mime) {
  const m = (mime || "").toLowerCase();
  if (m.includes("png")) return "png";
  if (m.includes("jpeg") || m.includes("jpg")) return "jpeg";
  return ""; // ExcelJS ne supporte pas bien webp
}

export function sanitizePart(s) {
  return String(s || "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_-]/g, "");
}

export function makeShotImageFilename(sceneIdx, shotNumber, mime) {
  const sc = String(sceneIdx || 0).padStart(2, "0");
  const sn = sanitizePart(shotNumber || "S0");
  const ext = extFromMime(mime);
  return `SC${sc}_${sn}.${ext}`;
}

export function dataUrlToBase64(dataUrl) {
  const i = String(dataUrl || "").indexOf(",");
  return i >= 0 ? String(dataUrl).slice(i + 1) : "";
}

export function parseSceneHeadingParts(sceneTitle) {
  const t0 = (sceneTitle || "").trim();
  if (!t0) return { intExt: "", location: "", timeOfDay: "" };

  const t = t0
    .replace(/^SC\s*\d+\s*[.:\-]?\s*/i, "")
    .replace(/^(SCENE|SCÈNE)\s*\d+\s*[.:\-]?\s*/i, "")
    .trim();

  const m = t.match(
    /^(INT\.?\/EXT\.?|INT\/EXT|I\/E|INT\.?|EXT\.?|INT|EXT)\b\.?\s*(.*)$/i
  );

  const intExt = (m?.[1] || "").replace(/\./g, "").toUpperCase();
  const rest = (m?.[2] || t).trim();

  const parts = rest.split(/\s*-\s*/).filter(Boolean);
  if (parts.length <= 1) return { intExt, location: rest, timeOfDay: "" };

  const timeOfDay = parts[parts.length - 1].trim();
  const location = parts.slice(0, -1).join(" - ").trim();
  return { intExt, location, timeOfDay };
}

export function getExportHeaders(lang) {
  const t = (k, vars) => translate(lang, k, vars);
  return [
    t("export.col.sceneIndex"),
    t("export.col.shotNumber"),
    t("export.col.intExt"),
    t("export.col.location"),
    t("export.col.timeOfDay"),
    t("export.col.shotSize"),
    t("export.col.angle"),
    t("export.col.lensMm"),
    t("export.col.focus"),
    t("export.col.framing"),
    t("export.col.movement"),
    t("export.col.lights"),
    t("export.col.durationSec"),
    t("export.col.description"),
    t("export.col.ratio"),
    t("export.col.colorFormat"),
    t("export.col.image"),
    t("export.col.scriptText"),
    t("export.col.master"),
  ];
}

export function buildDecoratedShots({
  shots,
  scenes,
  scriptText,
  lang = "en",
  shotPrefixMode,
  shotFrameFormat,
}) {
  const sceneIndexById = new Map();
  const sceneLabelById = new Map();
  (scenes || []).forEach((s, idx) => {
    sceneIndexById.set(s.id, idx + 1);
    sceneLabelById.set(s.id, getSceneNumberLabel(s, idx));
  });

  const decorated = (shots || []).map((sh) => {
    const sceneIdx = sceneIndexById.get(sh.sceneId) ?? 0;
    const sceneLabel = sceneLabelById.get(sh.sceneId) ?? String(sceneIdx);
    const scene = (scenes || []).find((s) => s.id === sh.sceneId) || null;

    const start = safeInt(sh.scriptRange?.start, 0);
    const end = safeInt(sh.scriptRange?.end, 0);
    const a = Math.max(0, Math.min(start, end));
    const b = Math.max(0, Math.max(start, end));

    const scriptSel = (scriptText || "").slice(a, b).trim();
    const heading = parseSceneHeadingParts(scene?.title || "");

    const { dataUrl, type } = inferImageInfo(sh);
    const imageFilename = dataUrl
      ? makeShotImageFilename(sceneIdx, sh.shotNumber, type)
      : "";

    const ratioLabel = getShotFrameFormat(
      effectiveFrameFormatValue(sh, scene, shotFrameFormat)
    ).short;
    const colorLabel = translate(lang, getShotColorFormat(effectiveColorFormatValue(sh, scene)).labelKey);

    return {
      sceneIdx,
      sceneLabel,
      isMaster: !!sh.isMaster,
      ratioLabel,
      colorLabel,
      shotNumber: getShotDisplayNumber(sh.shotNumber, lang, shotPrefixMode),
      rawShotNumber: sh.shotNumber || "",
      intExt: heading.intExt,
      location: heading.location,
      timeOfDay: heading.timeOfDay,

      shotSize: sh.shotSize || "",
      angle: sh.angle || sh.shotType || "",
      lensMm: String(sh.lensMm ?? "").trim(),
      focus: sh.focus || "",
      framing: sh.framing || "",
      movement: sh.movement || "",
      lights: String(sh.lights ?? "").trim(),

      durationSec: safeInt(sh.durationEstimateSec, 0),
      description: (sh.description || "").trim(),

      imageFilename,
      imageDataUrl: dataUrl,
      imageType: type,

      scriptText: scriptSel,
      // ✅ ordre = ordre d'affichage (listOrder), sinon numérotation, sinon position texte
      sortKey: Number.isFinite(sh.listOrder)
        ? sh.listOrder
        : Number.isFinite(sh.order)
        ? sh.order
        : a,
    };
  });

  decorated.sort((a, b) =>
    a.sceneIdx !== b.sceneIdx ? a.sceneIdx - b.sceneIdx : a.sortKey - b.sortKey
  );

  return decorated;
}

export function addImagesToZip(imagesFolder, decorated) {
  for (const r of decorated || []) {
    if (!r.imageDataUrl || !r.imageFilename) continue;
    const base64 = dataUrlToBase64(r.imageDataUrl);
    if (!base64) continue;
    imagesFolder.file(r.imageFilename, base64, { base64: true });
  }
}
